// Player commands: the only way a player changes the game. The server runs these for online play and the
// local runner runs them for solo play. Every command is validated here; nothing from a client is trusted.
import {
  G, CFG, S, stationsOf, emit, claimRegion, netChanged, createLine, removeLine, takeTrain, addCarriage, upgradeable,
  snapToLine, validSpot, buildDepot, insertIntoLine, removeStop, addTrain, depotCost, upgradeCost, releaseSurge, surgeReady, annex, ruinsNextTo,
  eliminate, hops,
} from './game.js';

const notice = (p, text) => emit({ type: 'notice', to: p.id, text });
const isId = v => Number.isInteger(v) && v >= 0;
const isNum = v => typeof v === 'number' && Number.isFinite(v);
const own = (p, id) => { const s = isId(id) && S(id); return s && s.owner === p.id ? s : null; };
const ownLine = (p, id) => p.lines.find(L => L.id === id) || null;
function newLine(p, a, b) {
  if (p.lines.length >= p.lineLimit) { notice(p, 'No free lines. Remove one, or buy another.'); return false; }
  const NL = createLine(p, [a.id, b.id]);
  p.lastLine = NL.id;
  emit({ type: 'linked', to: p.id, line: NL.id });
  return true;
}
// work on one line from its end at a: on to a new station b, back onto the stop before (shortening it), or round
// to its other end to close a loop. `told`: the player chose this line, so say why a drag does nothing.
function alterLine(p, L, a, b, told) {
  const n = L.st.length, first = L.st[0], last = L.st[n - 1];
  const say = text => { if (told) notice(p, text); return false; };
  if (L.loop) return say('That line is a loop. Drag a stop off it, or the middle of it onto a station.');
  if (first !== a.id && last !== a.id) return say('Drag from an end of the selected line, or pick a free line to build a new one.');
  const done = () => { netChanged(p); p.lastLine = L.id; emit({ type: 'linked', to: p.id, line: L.id }); return true; };
  if (!L.st.includes(b.id)) {
    if (last === a.id) L.st.push(b.id);
    else { L.st.unshift(b.id); for (const T of L.trains) { T.idx++; if (T.to != null) T.to++; } }
    return done();
  }
  const back = last === a.id ? L.st[n - 2] : L.st[1];
  if (b.id === back) {
    if (n <= 2) return say('A line needs two stops. Remove it with × instead.');
    removeStop(p, L, last === a.id ? n - 1 : 0);
    return done();
  }
  if (n >= 3 && ((first === a.id && last === b.id) || (last === a.id && first === b.id))) {
    L.loop = true; L.trains.forEach((T, i) => { T.dir = i % 2 ? -1 : 1; });
    netChanged(p); notice(p, 'Loop closed.');
    return true;
  }
  return say('That station is already on this line.');
}

const HANDLERS = {
  // draft: claim a starting region
  claim(p, c, seat) { return isId(c.rid) && claimRegion(seat, c.rid); },

  // drag from station a to station b. Which line it works on: the one this drag is already on, else the one the
  // player has selected (a free slot, 'new', means build a new line), else a line that ends at a, else a new line.
  link(p, c) {
    const a = own(p, c.a), b = own(p, c.b);
    if (!a || !b || a === b) return false;
    const cur = ownLine(p, p.lastLine);
    if (cur) return alterLine(p, cur, a, b, true);
    if (c.line === 'new') return newLine(p, a, b);
    const sel = ownLine(p, c.line);
    if (sel) return alterLine(p, sel, a, b, true);
    const L = p.lines.find(L => !L.loop && (L.st[0] === a.id || L.st[L.st.length - 1] === a.id));
    return (L && alterLine(p, L, a, b, false)) || newLine(p, a, b);
  },
  endDrag(p) { p.lastLine = null; return true; },
  // drag a stop off a line: the line closes up behind it. Dropped on another station, that one takes its place.
  moveStop(p, c) {
    const L = ownLine(p, c.line), s = own(p, c.station);
    if (!L || !s) return false;
    const i = L.st.indexOf(s.id);
    if (i < 0) return false;
    const to = c.to == null ? null : own(p, c.to);
    if (c.to != null && (!to || L.st.includes(to.id))) return false;
    if (!to && L.st.length <= 2) { notice(p, 'A line needs two stops. Remove it with × instead.'); return false; }
    if (to) {
      // the new station takes the old one's place; trains bound for it pause and set off again for the new one
      L.st[i] = to.id;
      for (const T of L.trains) if (T.idx === i || T.to === i) { T.to = null; T.wait = 0.2; }
      netChanged(p);
    } else removeStop(p, L, i);
    p.lastLine = L.id;
    emit({ type: 'linked', to: p.id, line: L.id });
    return true;
  },
  // drag a train onto another of your lines, or off the network into the sidings (+ Train brings it back free)
  moveTrain(p, c) {
    const L = ownLine(p, c.line), T = L && isId(c.train) && L.trains[c.train];
    if (!T) return false;
    const to = c.to == null ? null : ownLine(p, c.to);
    if (c.to != null && (!to || to === L)) return false;
    if (L.trains.length <= 1) { notice(p, 'Every line keeps at least one train.'); return false; }
    const here = S(L.st[T.idx]); if (here) here.units.push(...T.units);
    L.trains.splice(c.train, 1);
    if (to) {
      const at = isId(c.at) && c.at < to.st.length ? c.at : 0;
      const NT = addTrain(to, at); NT.cap = T.cap;
      notice(p, 'Train moved to another line.');
    } else {
      p.spare = [...(p.spare || []), T.cap].sort((x, y) => y - x);
      notice(p, 'Train sent to the sidings: + Train brings it back, free.');
    }
    netChanged(p);
    return true;
  },
  // drag a stretch of line onto a station: the line bends to call there, between the two stops it joined
  insertStop(p, c) {
    const L = ownLine(p, c.line), s = own(p, c.station);
    if (!L || !s || !isId(c.seg) || L.st.includes(s.id)) return false;
    const n = L.st.length;
    if (c.seg >= (L.loop ? n : n - 1)) return false;
    insertIntoLine(p, L, c.seg, s);
    p.lastLine = L.id;
    emit({ type: 'linked', to: p.id, line: L.id });
    return true;
  },
  removeLine(p, c) {
    const L = ownLine(p, c.line); if (!L) return false;
    const kept = removeLine(p, L);
    notice(p, kept ? `Line removed. ${kept} train${kept > 1 ? 's' : ''} kept as spare${kept > 1 ? 's' : ''}: + Train uses ${kept > 1 ? 'them' : 'it'} first, free.` : 'Line removed.');
    return true;
  },

  // depots and recyclers: dropped onto a stretch of line joins that line
  build(p, c) {
    if (c.kind !== 'depot' && c.kind !== 'recycler') return false;
    if (!G.players.some(q => q !== p && q.shape === c.shape) || !isNum(c.x) || !isNum(c.y)) return false;
    const pt = { x: c.x, y: c.y }, snap = snapToLine(p, pt), spot = snap ? snap.pos : pt;
    if (!validSpot(p, spot)) { notice(p, snap ? 'Not enough room on that stretch of line.' : 'Too close to the border or another station.'); return false; }
    const d = buildDepot(p, c.shape, spot, c.kind);
    if (!d) return false;
    if (snap) { insertIntoLine(p, snap.L, snap.i, d); notice(p, `${c.kind === 'recycler' ? 'Recycler' : 'Depot'} added to the line.`); }
    return true;
  },
  upgradeStation(p, c) {
    const s = own(p, c.station), cost = c.kind === 'warehouse' ? CFG.warehouseCost : c.kind === 'customs' ? CFG.customsCost : null;
    if (!s || cost == null || s[c.kind] || p.coins < cost) return false;
    if (c.kind === 'customs' && s.kind !== 'gate') return false;
    p.coins -= cost; s[c.kind] = true;
    notice(p, c.kind === 'customs' ? 'Customs post open: 3x toll, rivals cross slowly.' : 'Warehouse built: +10 platform space.');
    return true;
  },

  addTrain(p, c) {
    const L = ownLine(p, c.line) || p.lines.slice().sort((x, y) => x.trains.length - y.trains.length)[0];
    const how = L && takeTrain(p, L);
    if (!how) return false;
    notice(p, (how === 'spare' ? `Spare train back in service (${L.trains[L.trains.length - 1].cap} seats)` : 'Train added') + (L.loop ? ', running the other way round.' : '.'));
    return true;
  },
  addCarriage(p, c) {
    const L = ownLine(p, c.line);
    if (!L) { notice(p, 'Click a line first, then add a carriage to it.'); return false; }
    if (!upgradeable(L).length) return false;
    const T = addCarriage(p, L);
    if (T) notice(p, `Carriage added. That train now seats ${T.cap}.`);
    return !!T;
  },
  buyLine(p) {
    if (p.coins < CFG.lineCost || p.lineLimit >= CFG.lineMax) return false;
    p.coins -= CFG.lineCost; p.lineLimit++;
    return true;
  },

  target(p, c) {
    if (c.v === 'auto') { p.target = 'auto'; return true; }
    const q = isId(c.v) && G.players[c.v];
    if (!q || q === p || !q.alive || hops(p.region, q.region) == null) return false;
    p.target = q.id; return true;
  },
  rate(p, c) {
    if (c.delta !== 1 && c.delta !== -1) return false;
    p.rate = Math.max(1, Math.min(p.rateMax, p.rate + c.delta));
    return true;
  },
  upgradeRate(p) {
    if (p.rateMax >= CFG.rateCap || p.coins < upgradeCost(p)) return false;
    p.coins -= upgradeCost(p); p.rateMax++; p.rate = p.rateMax;
    return true;
  },
  bank(p) { p.banking = !p.banking; return true; },
  surge(p) {
    if (!surgeReady(p)) { notice(p, p.bank < CFG.surgeMin ? `A surge needs at least ${CFG.surgeMin} banked shapes.` : 'Your next surge is not ready yet.'); return false; }
    if (!releaseSurge(p)) { notice(p, 'No rival to send a surge to right now.'); return false; }
    return true;
  },
  annex(p, c) { return isId(c.rid) && ruinsNextTo(p).some(r => r.id === c.rid) && annex(p, c.rid); },

  // testing helpers, only in rooms created with cheats on
  cheat(p, c) {
    if (!G.cheats) return false;
    if (c.what === 'coins' && (c.n === 100 || c.n === 1000)) { p.coins += c.n; return true; }
    if (c.what === 'rush') { G.time = Math.max(G.time, CFG.rushAt); return true; }
    if (c.what === 'kill') {
      const rivals = G.players.filter(q => q.alive && q !== p);
      if (!rivals.length) return false;
      rivals.sort((a, b) => (hops(p.region, a.region) ?? 99) - (hops(p.region, b.region) ?? 99));
      const v = rivals[0], st = stationsOf(v)[0];
      if (st) st.units.push({ id: G.uid++, shape: p.shape, sender: p.id, target: v.id });   // credit the kill
      eliminate(v);
      return true;
    }
    return false;
  },
};
export const COMMAND_TYPES = Object.keys(HANDLERS);

// returns true when the command changed the game
export function applyCommand(seat, cmd) {
  // own properties only: '__proto__' or 'constructor' must never reach a handler lookup
  if (!cmd || typeof cmd !== 'object' || typeof cmd.type !== 'string' || !Object.hasOwn(HANDLERS, cmd.type)) return false;
  if (cmd.type === 'claim') return G.phase === 'draft' && HANDLERS.claim(null, cmd, seat);
  if (G.phase !== 'play') return false;
  const p = G.players[seat];
  if (!p || !p.alive) return false;
  return !!HANDLERS[cmd.type](p, cmd);
}
